<template>
  <div class="w-[320px]">
    <div
      v-if="state.showInformationInDetail"
      class="p-4 mb-2 bg-neutral-secondary px-4 mt-2 rounded-md"
    >
      <NText variant="caption1" class="mt-3 block text-neutral-secondary-press">
        {{ ipoConfig.description }}
      </NText>
    </div>

    <div v-else class="bg-neutral-secondary px-4 py-3 rounded-md">
      <div class="border-b border-neutral-secondary">
        <div class="flex items-center justify-between h-[32px]">
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-press"
          >
            {{ t('ipo.requestType') }}
          </NText>

          <div>
            <NText variant="caption1" weight="medium" class="align-middle">
              {{ requestType }}
            </NText>
          </div>
        </div>

        <div class="flex items-center justify-between h-[32px]">
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-press"
          >
            {{ t('ipo.requestSide') }}
          </NText>

          <div>
            <NText
              variant="caption1"
              weight="medium"
              class="align-middle text-brand-primary"
            >
              {{ t('common.buy') }}
            </NText>
          </div>
        </div>

        <div class="flex items-center justify-between h-[32px]">
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-press"
          >
            {{ t('ipo.requestStatus') }}
          </NText>

          <div>
            <NText
              variant="caption1"
              weight="medium"
              class="align-middle"
              :class="state.stateNameColor"
            >
              {{ state.stateName }}
            </NText>
          </div>
        </div>
      </div>

      <div class="border-b border-neutral-secondary last:border-none">
        <div class="flex items-center justify-between h-[32px]">
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-press"
          >
            {{ t('ipo.requestNumber') }}
          </NText>

          <NumberFormatter
            #="{ number }"
            :value="
              !offer.hasPreOrder && offer.isAutomaticSubmit
                ? offer.maximumOrderQuantity
                : offer.quantity
            "
          >
            <NText variant="caption1" weight="bold">
              {{ number }}
            </NText>
          </NumberFormatter>
        </div>

        <div class="flex items-center justify-between h-[32px]">
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-press"
          >
            {{ t('ipo.requestPrice') }}
          </NText>

          <NumberFormatter
            v-if="state.offerHasPrice"
            #="{ number }"
            :value="
              (!offer.hasPreOrder && offer.isAutomaticSubmit) ||
              (offer.hasPreOrder && offer.price === SPECIAL_PRICE)
                ? offer.maximumOrderPrice
                : offer.price
            "
          >
            <NText variant="caption1" weight="bold">
              {{ number }}

              <NText
                variant="caption1"
                class="text-neutral-secondary text-left align-middle"
              >
                {{ t('misc.rial') }}
              </NText>
            </NText>
          </NumberFormatter>

          <NText v-else variant="caption1" weight="medium" class="align-middle">
            {{ t('ipo.discoveredInTheFirstStage') }}
          </NText>
        </div>

        <div
          v-if="state.offerHasPrice"
          class="flex items-center justify-between h-[32px]"
        >
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-press"
          >
            {{ t('ipo.requestValue') }}
          </NText>

          <NumberFormatter #="{ number }" :value="totalValue" round>
            <NText variant="caption1" weight="bold">
              {{ number }}

              <NText
                variant="caption1"
                class="text-neutral-secondary text-left align-middle"
              >
                {{ t('misc.rial') }}
              </NText>
            </NText>
          </NumberFormatter>
        </div>
      </div>

      <div
        v-if="state.showRequestTime"
        class="flex items-center justify-between h-[32px]"
      >
        <NText
          variant="caption1"
          weight="medium"
          class="text-neutral-secondary-press"
        >
          {{ t('ipo.submitRequestTime') }}
        </NText>

        <div>
          <NText variant="caption1" weight="medium" class="align-middle">
            {{ submitTime }}
          </NText>
        </div>
      </div>
    </div>

    <div class="flex space-x-4 space-x-reverse pt-6">
      <NButton
        v-if="state.showDepositButton"
        variant="flat"
        color="gray"
        class="flex-1 font-bold"
        @click="openDepositDialog"
      >
        {{ t('common.deposit') }}
      </NButton>

      <NButton
        v-if="state.showCancelRequestButton"
        variant="outline"
        color="red"
        class="flex-1 font-bold"
        @click="
          openDeleteOfferDialog({
            offer,
          })
        "
      >
        {{ t('ipo.cancelRequest') }}
      </NButton>

      <NButton
        v-if="state.showSubmitNewRequestButton"
        variant="outline"
        color="gray"
        class="flex-1 font-bold"
        @click="
          openBuyOfferDialog({
            offer,
          })
        "
      >
        {{ t('ipo.editRequest') }}
      </NButton>

      <NButton
        v-if="state.showCreateOrderButton"
        variant="outline"
        color="gray"
        class="flex-1 font-bold"
        @click="openBuyOfferDialog({ offer })"
      >
        {{ t('ipo.editRequest') }}
      </NButton>

      <div
        v-if="state.showRepurchaseOrderBox"
        class="flex flex-col w-full bg-neutral-secondary rounded-md p-2"
      >
        <NText class="pb-1 text-neutral-secondary-hover" variant="overline">
          {{ t('ipo.repurchaseRequestDescription') }}
        </NText>

        <div
          v-if="state.showRepurchaseOrderButton"
          class="flex flex-row-reverse"
        >
          <NButton
            size="mini"
            variant="flat"
            @click="
              openBuyOfferDialog({
                offer,
              })
            "
          >
            <NText variant="overline" weight="medium">
              {{ t('ipo.repurchaseRequest') }}
            </NText>
          </NButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { IPO_CONFIG, SPECIAL_PRICE, IPO_CONFIG_DEFAULT } from '@core/ipo';
import { useSymbolDeductions, calcSymbolBuyValue } from '@core/symbol';
import { InitialPublicOfferItem } from '@libs/api-reference';

import { useCashDialogs } from '@/modules/cash';
import { useIpoDialogs } from '@/modules/ipo';

import NumberFormatter from '@/components/Number.vue';

type OfferDetailDialogProps = {
  offer: InitialPublicOfferItem;
  state: Record<string, any>;
};

const props = defineProps<OfferDetailDialogProps>();

const { openDepositDialog } = useCashDialogs();
const { openBuyOfferDialog, openDeleteOfferDialog } = useIpoDialogs();

const { t, d } = useI18n();
const { findWageConfigBySymbol } = useSymbolDeductions();

const wage = findWageConfigBySymbol(props.offer);

const requestType = computed(() => {
  if (props.offer.hasPreOrder) {
    return t('ipo.preOrder');
  }

  if (props.offer.isAutomaticSubmit) {
    return t('ipo.buyOrderAutomatically');
  }

  return '';
});

const submitTime = computed(() =>
  props.offer.submitDate
    ? `${d(props.offer.submitDate)} ${d(props.offer.submitDate, 'shortTime')}`
    : ''
);

const totalValue = computed(() => {
  if (!props.offer.hasPreOrder && props.offer.isAutomaticSubmit) {
    return calcSymbolBuyValue(
      {
        quantity: Number(props.offer.maximumOrderQuantity),
        price: Number(props.offer.maximumOrderPrice),
      },
      wage
    );
  }

  return calcSymbolBuyValue(
    {
      quantity: Number(props.offer.quantity),
      price:
        props.offer.hasPreOrder && props.offer.price === SPECIAL_PRICE
          ? Number(props.offer.maximumOrderPrice)
          : Number(props.offer.price),
    },
    wage
  );
});

const ipoConfig = computed(() => {
  return (
    IPO_CONFIG.find((item) => item.isin === props.offer.instrumentIsin) ||
    IPO_CONFIG_DEFAULT
  );
});
</script>
